import React, { createContext, useCallback, useContext, useState } from "react";

import { cn } from "../lib/utils";

interface RadioGroupContextValue {
  value: string;
  onSelect: (value: string) => void;
  name: string;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

function useRadioGroup() {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error("RadioGroupItem must be used within a RadioGroup");
  }
  return ctx;
}

let groupCounter = 0;

export function RadioGroup({
  className,
  value: controlledValue,
  defaultValue = "",
  onValueChange,
  disabled,
  children,
}: {
  className?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [name] = useState(() => `radio-group-${++groupCounter}`);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : uncontrolledValue;

  const onSelect = useCallback(
    (val: string) => {
      if (!isControlled) {
        setUncontrolledValue(val);
      }
      onValueChange?.(val);
    },
    [isControlled, onValueChange],
  );

  return (
    <RadioGroupContext.Provider
      value={{ value: currentValue, onSelect, name, disabled }}
    >
      <div role="radiogroup" className={className}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

export function RadioGroupItem({
  value,
  id,
  className,
  disabled: itemDisabled,
}: {
  value: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}) {
  const {
    value: selectedValue,
    onSelect,
    name,
    disabled: groupDisabled,
  } = useRadioGroup();
  const checked = selectedValue === value;
  const disabled = itemDisabled || groupDisabled;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      id={id}
      className={cn(
        "border-primary text-primary ring-offset-background focus-visible:ring-ring aspect-square h-4 w-4 shrink-0 rounded-full border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      onClick={() => {
        if (!disabled) {
          onSelect(value);
        }
      }}
    >
      {checked && (
        <span className="flex items-center justify-center">
          <span className="bg-primary h-2.5 w-2.5 rounded-full" />
        </span>
      )}
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onSelect(value)}
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />
    </button>
  );
}
